import React from "react";
import dayjs from "dayjs";
import {
  Box, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, Text, useColorModeValue,
} from "@chakra-ui/react";
import { actionLabel } from "./auditLogConstants";
import { CODE_BLOCK } from "./auditLogStyles";

/**
 * Formats a JSON payload for display, or a dash when empty.
 * @param {*} payload - The payload_before / payload_after value
 * @returns {string} Pretty-printed JSON or a dash
 */
function formatPayload(payload) {
  if (payload === null || payload === undefined) return "—";
  try {
    return JSON.stringify(payload, null, 2);
  } catch (error) {
    return String(payload);
  }
}

/**
 * Renders a single label/value metadata row.
 * @param {Object} props - label, value
 * @returns {React.ReactElement}
 */
function MetaRow({ label, value }) {
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Flex justify="space-between" gap="16px" py="6px">
      <Text fontSize="xs" color={labelColor}>{label}</Text>
      <Text fontSize="sm" color={valueColor} textAlign="right" wordBreak="break-word">{value ?? "—"}</Text>
    </Flex>
  );
}

/**
 * Renders all metadata rows for an audit entry.
 * @param {Object} props - row (the audit entry)
 * @returns {React.ReactElement}
 */
function MetaSection({ row }) {
  return (
    <Box>
      <MetaRow label="Date" value={dayjs(row.created_at).format("DD/MM/YYYY HH:mm:ss")} />
      <MetaRow label="Admin" value={`${row.admin_email || "—"} (#${row.admin_id ?? "?"}, role ${row.admin_role ?? "?"})`} />
      <MetaRow label="Action" value={actionLabel(row.action)} />
      <MetaRow label="Cible user" value={row.target_user_id} />
      <MetaRow label="Ressource" value={row.target_resource_id ? `${row.target_resource} #${row.target_resource_id}` : row.target_resource} />
      <MetaRow label="Requête" value={`${row.request_method || "—"} ${row.request_path || ""}`} />
      <MetaRow label="Statut HTTP" value={row.response_status} />
      <MetaRow label="Motif" value={row.reason} />
      <MetaRow label="IP" value={row.ip_address} />
      <MetaRow label="User-Agent" value={row.user_agent} />
    </Box>
  );
}

/**
 * Detail drawer showing the full audit entry: metadata, request info and the
 * before/after payloads.
 * @param {Object} props - row, onClose
 * @returns {React.ReactElement}
 */
function AuditLogDetailDrawer({ row, onClose }) {
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const codeBg = useColorModeValue("secondaryGray.100", "navy.800");
  const codeColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Drawer isOpen={!!row} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Entrée d'audit {row ? `#${row.id}` : ""}</DrawerHeader>
        <DrawerBody>
          {row && (
            <Box>
              <MetaSection row={row} />
              <Divider my="14px" />
              <Text fontSize="xs" fontWeight="700" color={labelColor} mb="6px">PAYLOAD AVANT</Text>
              <Box {...CODE_BLOCK} bg={codeBg} color={codeColor}>{formatPayload(row.payload_before)}</Box>
              <Text fontSize="xs" fontWeight="700" color={labelColor} mt="14px" mb="6px">PAYLOAD APRÈS</Text>
              <Box {...CODE_BLOCK} bg={codeBg} color={codeColor}>{formatPayload(row.payload_after)}</Box>
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default AuditLogDetailDrawer;
